import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { TreePine, X, ChevronLeft, ChevronRight, Filter, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Photos' },
    { id: 'cabin', label: 'The Cabin' },
    { id: 'nature', label: 'Nature' },
    { id: 'activities', label: 'Activities' }
  ];

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (err) {
      console.error('Error fetching gallery:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = activeCategory === 'all' 
    ? images 
    : images.filter(img => img.category === activeCategory);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % filteredImages.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, filteredImages.length]);

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-900/95 border-b border-stone-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <TreePine className="w-8 h-8 text-emerald-400" />
            <span className="text-xl font-semibold text-white">
              Stay <span className="text-emerald-400">Tran-Quil</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-stone-300 hover:text-white transition-colors">Home</Link>
            <Link to="/things-to-do" className="text-stone-300 hover:text-white transition-colors">Things To Do</Link>
            <Link to="/book">
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full px-6">
                Book Now
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <span className="text-emerald-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            Photo Gallery
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Glimpses of Paradise
          </h1>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto">
            Explore our beautiful riverfront property through these photos
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === cat.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-stone-400">No photos found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredImages.map((image, index) => (
              <div
                key={image.image_id}
                onClick={() => openLightbox(index)}
                className={`relative overflow-hidden rounded-2xl cursor-pointer group ${
                  index === 0 ? 'sm:col-span-2 sm:row-span-2' : ''
                }`}
              >
                <img
                  src={image.url}
                  alt={image.caption || 'Gallery image'}
                  className={`w-full object-cover transition-transform duration-500 group-hover:scale-110 ${
                    index === 0 ? 'h-64 sm:h-full' : 'h-64'
                  }`}
                  loading="lazy"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    {image.caption && (
                      <p className="text-white font-medium">{image.caption}</p>
                    )}
                    <p className="text-stone-300 text-sm capitalize">{image.category}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-stone-400 mb-4">Ready to experience it for yourself?</p>
          <Link to="/book">
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-6 rounded-full text-lg">
              Book Your Stay
            </Button>
          </Link>
        </div>
      </main>

      {/* Lightbox */}
      {lightboxOpen && filteredImages.length > 0 && (
        <div className="fixed inset-0 z-50 bg-stone-950/98 backdrop-blur-sm flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/60 hover:text-white p-2 z-10"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Navigation */}
          <button
            onClick={prevImage}
            className="absolute left-4 md:left-8 text-white/60 hover:text-white p-3 hover:bg-white/10 rounded-full transition-colors z-10"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          <button
            onClick={nextImage}
            className="absolute right-4 md:right-8 text-white/60 hover:text-white p-3 hover:bg-white/10 rounded-full transition-colors z-10"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Main Image */}
          <div className="max-w-6xl max-h-[85vh] mx-4">
            <img
              src={filteredImages[currentIndex]?.url}
              alt={filteredImages[currentIndex]?.caption || 'Gallery image'}
              className="max-w-full max-h-[75vh] object-contain rounded-lg mx-auto"
            />
            {filteredImages[currentIndex]?.caption && (
              <p className="text-white text-center mt-4 text-lg">
                {filteredImages[currentIndex].caption}
              </p>
            )}
          </div>

          {/* Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60">
            {currentIndex + 1} / {filteredImages.length}
          </div>

          {/* Thumbnails */}
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80vw] overflow-x-auto pb-2">
            {filteredImages.map((img, index) => (
              <button
                key={img.image_id}
                onClick={() => setCurrentIndex(index)}
                className={`shrink-0 w-16 h-12 rounded overflow-hidden transition-all ${
                  index === currentIndex
                    ? 'ring-2 ring-emerald-400 opacity-100'
                    : 'opacity-50 hover:opacity-80'
                }`}
              >
                <img
                  src={img.url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
